import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { CategoryKey, LanguageCode, ShiurAudience } from '../../types';
import { CATEGORIES, LANGUAGE_CODES, SHIUR_AUDIENCES, getCategory } from '../../config/categories';
import {
  CATEGORY_EXTRA_FILTERS,
  DirectoryFilters,
  FreeFilter,
  hasActiveFilters,
} from '../../utils/directorySearch';

interface Props {
  /** The category page this bar is on; undefined = All Services. */
  category?: CategoryKey;
  filters: DirectoryFilters;
  onChange: (change: Partial<DirectoryFilters>, options?: { replace?: boolean }) => void;
  onClear: () => void;
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap rounded-full px-3 min-h-9 text-sm border transition-colors ${
        active ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink border-ink/15 hover:border-ink/40'
      }`}
    >
      {children}
    </button>
  );
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export default function DirectoryFilterBar({ category, filters, onChange, onClear }: Props) {
  const { t } = useTranslation();

  // Typed text is kept locally so the URL isn't rewritten on every key press.
  const [text, setText] = useState(filters.q);
  useEffect(() => setText(filters.q), [filters.q]);
  useEffect(() => {
    if (text === filters.q) return;
    const id = setTimeout(() => onChange({ q: text }, { replace: true }), 250);
    return () => clearTimeout(id);
  }, [text, filters.q, onChange]);

  const config = category ? getCategory(category) : undefined;
  const subcategories = config?.subcategories ?? [];
  const extras = category ? CATEGORY_EXTRA_FILTERS[category] : [];
  const showOpenNow = !config || config.usesHours;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-ash pointer-events-none" aria-hidden />
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('directory.filter.search')}
          aria-label={t('directory.filter.search')}
          className="w-full ps-9 pe-3 min-h-11 rounded-[10px] border border-ink/15 bg-paper text-ink placeholder:text-ash focus:outline-none focus:ring-2 focus:ring-ink/30 focus:border-ink text-sm"
        />
      </div>

      {/* Chips scroll sideways on small phones instead of wrapping into many rows. */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
        {showOpenNow && (
          <Chip active={filters.open} onClick={() => onChange({ open: !filters.open })}>
            {t('directory.filter.openNow')}
          </Chip>
        )}

        {extras.includes('today') && (
          <Chip active={filters.today} onClick={() => onChange({ today: !filters.today })}>
            {t('directory.shiur.filter.today')}
          </Chip>
        )}

        {!category &&
          CATEGORIES.map((c) => (
            <Chip
              key={c.key}
              active={filters.cat.includes(c.key)}
              onClick={() => onChange({ cat: toggle(filters.cat, c.key) })}
            >
              {t(`directory.categoryPlural.${c.key}`)}
            </Chip>
          ))}

        {subcategories.map((sub) => (
          <Chip key={sub} active={filters.sub.includes(sub)} onClick={() => onChange({ sub: toggle(filters.sub, sub) })}>
            {t(`directory.sub.${sub}`)}
          </Chip>
        ))}

        {extras.includes('aud') &&
          SHIUR_AUDIENCES.filter((a) => a !== 'everyone').map((a: ShiurAudience) => (
            <Chip key={a} active={filters.aud === a} onClick={() => onChange({ aud: filters.aud === a ? undefined : a })}>
              {t(`directory.audience.${a}`)}
            </Chip>
          ))}

        {extras.includes('accessible') && (
          <Chip active={filters.accessible} onClick={() => onChange({ accessible: !filters.accessible })}>
            {t('directory.field.accessible')}
          </Chip>
        )}

        {extras.includes('free') &&
          (['free', 'rent'] as FreeFilter[]).map((v) => (
            <Chip
              key={v}
              active={filters.free === v}
              onClick={() => onChange({ free: filters.free === v ? undefined : v })}
            >
              {t(`directory.filter.${v}`)}
            </Chip>
          ))}
      </div>

      {(extras.includes('lang') || hasActiveFilters(filters)) && (
        <div className="flex items-center gap-3 flex-wrap">
          {extras.includes('lang') && (
            <select
              value={filters.lang ?? ''}
              onChange={(e) => onChange({ lang: (e.target.value || undefined) as LanguageCode | undefined })}
              aria-label={t('directory.field.languages')}
              className="min-h-9 rounded-[10px] border border-ink/15 bg-paper text-ink text-sm px-2"
            >
              <option value="">{t('directory.filter.anyLanguage')}</option>
              {LANGUAGE_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(`directory.language.${code}`)}
                </option>
              ))}
            </select>
          )}
          {hasActiveFilters(filters) && (
            <button
              type="button"
              onClick={() => {
                setText('');
                onClear();
              }}
              className="inline-flex items-center gap-1 text-sm text-ash hover:text-ink min-h-9"
            >
              <X className="w-4 h-4" aria-hidden />
              {t('directory.filter.clear')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
